import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core'
import { every, isArray, some } from 'lodash'
import { first, map, Subject, takeUntil } from 'rxjs'

import { ClinicModulesKeys } from '../model/clinic'
import { ClinicService } from '../services/clinic.service'

type Module = ClinicModulesKeys

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[appHasModule]',
  standalone: false,
})
export class HasModuleDirective implements OnInit, OnDestroy {
  @Input()
  public appHasModule: Module | Module[]

  @Input()
  public appHasModuleElse: TemplateRef<any> | null = null

  /**
   * To use this input, you need to pass it as an option in the directive selector:
   *
   * <div *appHasModule="['intake', 'eligibility']; operator: 'IN'">
   */
  @Input()
  public appHasModuleOperator: 'AND' | 'IN' | 'OR' = 'AND'

  private onDestroy$ = new Subject<void>()

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private clinicService: ClinicService,
  ) { }

  public ngOnInit() {
    this.clinicService.clinic$.pipe(
      takeUntil(this.onDestroy$),
      first(),
      map(clinic => {
        const modules = isArray(this.appHasModule) ? this.appHasModule : [this.appHasModule]
        if (this.appHasModuleOperator === 'AND') {
          return every(modules, module => clinic.modules[module])
        } else {
          return some(modules, module => clinic.modules[module])
        }
      }),
    ).subscribe(hasModule => this.handleVisibility(hasModule))
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private handleVisibility(hasModule: boolean) {
    this.viewContainer.clear()

    if (hasModule) {
      this.viewContainer.createEmbeddedView(this.templateRef)
    } else if (this.appHasModuleElse) {
      this.viewContainer.createEmbeddedView(this.appHasModuleElse)
    }
  }
}
